import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSales } from '../store/salesStore';
import { useAssetStore } from '../store/assetStore';
import { useAuth } from '../store/authStore';
import { useUploadStore } from '../store/uploadStore';
import { api, VendorStats } from '../lib/api';
import { toast } from 'sonner';

function Dashboard() {
  const { user, isAuthenticated, updateUser } = useAuth();
  const { stats, fetchStats } = useSales();
  const { total, fetchAssets } = useAssetStore();
  const uploadFile = useUploadStore((s) => s.uploadFile);
  const [loading, setLoading] = useState(true);
  const [savingBanner, setSavingBanner] = useState(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    Promise.all([
      fetchStats().catch((e: Error) => toast.error(e.message)),
      fetchAssets().catch(() => null),
    ])
      .then(() => {
        if (!cancelled) setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, fetchStats, fetchAssets]);

  const handleBannerSelect = async (file: File | undefined) => {
    if (!file || savingBanner) return;
    setSavingBanner(true);
    try {
      const result = await uploadFile(file);
      await api.profile.updateBanner(result.url);
      updateUser({ banner_url: result.url });
      toast.success('Storefront banner updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error(message);
    } finally {
      setSavingBanner(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">
          <div className="spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const s: VendorStats | null = stats;
  const balance =
    (s?.total_earnings ?? 0) -
    (s?.payouts ?? []).reduce(
      (sum, p) =>
        p.status === 'approved' || p.status === 'completed'
          ? sum + (p as { total_amount: number }).total_amount
          : sum,
      0
    );

  return (
    <div className="container">
      <h1 className="page-title">Welcome, {user?.name || 'Vendor'}</h1>
      <p className="page-subtitle">Overview of your asset store performance.</p>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Earnings</div>
          <div className="stat-value">€{(s?.total_earnings ?? 0).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Available Balance</div>
          <div className="stat-value success">€{Math.max(0, balance).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Assets</div>
          <div className="stat-value">{total ?? s?.total_assets ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Approvals</div>
          <div className="stat-value warning">{s?.pending_approvals ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Sales</div>
          <div className="stat-value">{s?.total_sales ?? 0}</div>
        </div>
      </div>

      <section className="section">
        <h2 className="page-title" style={{ fontSize: '1.4rem' }}>
          Storefront
        </h2>
        <div className="panel storefront-card">
          <div className="storefront-card-text">
            <p>
              Your storefront banner is shown at the top of your public vendor page, next to all of
              your products. Keep it at least 1200px wide.
            </p>
            <label className="file-btn btn btn--primary">
              {savingBanner ? 'Uploading…' : user?.banner_url ? 'Change banner' : 'Upload banner'}
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                disabled={savingBanner}
                onChange={(e) => handleBannerSelect(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="storefront-banner-preview">
            {user?.banner_url ? (
              <img src={user.banner_url} alt="Storefront banner" />
            ) : (
              <div className="storefront-banner-empty">No banner yet</div>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <h2 className="page-title" style={{ fontSize: '1.4rem' }}>
          Earnings by Asset
        </h2>
        {(s?.monthly_commissions ?? []).length === 0 ? (
          <div className="panel">
            <p className="empty-state">
              No sales yet. Upload assets and get approved to start earning.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Sales</th>
                  <th>Commission</th>
                  <th>Earned</th>
                </tr>
              </thead>
              <tbody>
                {(s?.monthly_commissions ?? []).map((c) => (
                  <tr key={c.asset_name}>
                    <td>{c.asset_name}</td>
                    <td>{c.sales_count}</td>
                    <td>{(c.commission_rate * 100).toFixed(0)}%</td>
                    <td>€{c.total_earned.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="toolbar" style={{ marginTop: '2rem' }}>
        <Link to="/assets/new" className="btn btn--primary">
          Upload a New Asset
        </Link>
        <Link to="/payouts" className="btn btn--secondary">
          Request a Payout
        </Link>
      </div>
    </div>
  );
}

export default Dashboard;
